// Portfolio.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPortfolio } from "../../api/client";
import "./Portfolio.css";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* Card thumbnail — real <img> when a URL exists, initials placeholder otherwise.
   Keeps the grid safe to render even before the backend image is uploaded. */
function ProjectThumb({ project }) {
  const [failed, setFailed] = useState(false);
  const showPlaceholder = !project.image || failed;

  if (showPlaceholder) {
    const initials = (project.client || project.title || "?")
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
    return (
      <div className="pf-thumb pf-thumb--placeholder">
        <span className="pf-thumb__initials">{initials}</span>
      </div>
    );
  }

  return (
    <img
      src={project.image}
      alt={project.title}
      className="pf-thumb"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function Portfolio() {
  const navigate = useNavigate();
  const [heroRef, heroIn] = useInView(0.1);
  const [gridRef, gridIn] = useInView(0.08);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  /* ────────────────────────────────────────────────────────────
     BACKEND FETCH — loads every project from the PHP backend
     (portfolio.php). Response shape: an array of objects like
       { id, image, client, title, desc, tags: [] }
     ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;

    fetchPortfolio()
      .then((data) => {
        if (!cancelled) {
          setProjects(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setProjects([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  const previewStrip = projects.slice(0, 4);
  const projectCount = projects.length;

  return (
    <div className="pf-page">

      {/* ── 1. HERO — creative, with a floating preview collage ── */}
      <section className={`pf-hero${heroIn ? " pf-in" : ""}`} ref={heroRef}>
        <div className="pf-hero__blob pf-hero__blob--1" aria-hidden="true" />
        <div className="pf-hero__blob pf-hero__blob--2" aria-hidden="true" />
        <div className="pf-hero__grid-overlay" aria-hidden="true" />

        <div className="pf-hero__content">
          <h1 className="pf-hero__heading pf-anim" style={{ animationDelay: "0.15s" }}>
            Projects that show<br />
            what we <span className="pf-accent">actually build.</span>
          </h1>
          <p className="pf-hero__sub pf-anim" style={{ animationDelay: "0.25s" }}>
            A selection of platforms, apps and systems we've shipped for clients
            across healthcare, retail, logistics and education.
          </p>

          <div className="pf-hero__stats pf-anim" style={{ animationDelay: "0.35s" }}>
            <div className="pf-hero__stat"><span>{projectCount}+</span>Projects</div>
            <div className="pf-hero__stat-sep" />
            <div className="pf-hero__stat"><span>28+</span>Partners</div>
            <div className="pf-hero__stat-sep" />
            <div className="pf-hero__stat"><span>6</span>Industries</div>
          </div>

          <div className="pf-hero__actions pf-anim" style={{ animationDelay: "0.45s" }}>
            <button className="pf-btn-primary" onClick={() => navigate("/contact")}>
              Start a Project <span className="pf-btn-arrow">→</span>
            </button>
          </div>
        </div>

        {/* floating collage — pulls thumbnails from the same live data */}
        <div className="pf-hero__collage pf-anim" style={{ animationDelay: "0.3s" }} aria-hidden="true">
          {previewStrip.map((p, i) => (
            <div key={p.id ?? p.title} className={`pf-collage__tile pf-collage__tile--${i}`}>
              <ProjectThumb project={p} />
            </div>
          ))}
        </div>
      </section>

      {/* ── 2. PORTFOLIO GRID — renders every project the backend returns ── */}
      <section className="pf-grid-section" ref={gridRef}>
        <div className="pf-section__head">
          <span className="pf-eyebrow">Selected Work</span>
          <h2 className="pf-heading">Our recent projects</h2>
        </div>

        {loading && (
          <p className="pf-state-msg">Loading projects...</p>
        )}

        {!loading && error && (
          <p className="pf-state-msg">Couldn't load projects right now. Please try again later.</p>
        )}

        {!loading && !projects.length && (
          <p className="pf-state-msg">No projects added yet.</p>
        )}

        <div className={`pf-grid${gridIn ? " pf-in" : ""}`}>
          {projects.map((p, i) => (
            <div key={p.id ?? p.title} className="pf-card" style={{ transitionDelay: `${i * 0.07}s` }}>
              <div className="pf-card__media">
                <ProjectThumb project={p} />
              </div>
              <div className="pf-card__body">
                <span className="pf-card__client">{p.client}</span>
                <h3 className="pf-card__title">{p.title}</h3>
                <p className="pf-card__desc">{p.desc}</p>
                <div className="pf-card__tags">
                  {(p.tags || []).map((t) => <span key={t} className="pf-tag">{t}</span>)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}